import { useMemo, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { ScrollText, Download } from 'lucide-react'
import {
  Card, CardHeader, EmptyState, ErrorState, FilterChip, Identifier, LoadingState,
  NoPermissionState, TBody, TD, TH, THead, Table, TableWrap, useLocale,
} from '@hk/ui'
import {
  activityActor, activitySubject, activityExportName, exportActivityCsv, formatCount,
  formatDateTime, may, pluralise, readActivity, translateError,
  type ActivityFilter, type ActivityRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { deviceLabel } from './UserDetail'

/* ============================================================================
   W-124 — THE ACTIVITY LOG. Who did what, kept for two years.

   Pack 5 gives this screen its whole shape in two sentences:

       "Overrides, permission changes and refused sign-ins have their own filters because
        those are the only reasons anybody opens a log."

       "A log you can only read chronologically is a log nobody reads."

   So the tabs are not a convenience, they are the interface. Everything else on this
   screen exists to make one of them land on the right rows.

   ── WHAT IS IN IT, AND THE DEPARTURE FROM THE PACK ───────────────────────────

   **ACTS OF AUTHORITY, NOT ACTS OF WORK.** An attendant finishing a clean is work; it has a
   task, a checklist, photographs and a room-history row, and every board and report is
   built on them. Logging it here too would make this table about 1.1 million rows over the
   retention period against roughly fifteen to forty a day for the acts that are actually
   recorded — and would bury the six rows a year somebody opens this screen to find.

   The pack's mock draws one row of ordinary work. That is **CQ-134**, with the arithmetic
   attached, because it is a question about a table that will be written for two years
   before anybody notices the answer was wrong.

   ── EVERY WORD IN THE TABLE IS A TOKEN THE SERVER SENT ───────────────────────

   The verb, the subject type, the source, and both of the middle columns arrive as
   SCREAMING_SNAKE and are turned into sentences here, in the reader's own language. That is
   the only way this screen can be in Arabic at all: half the values are chosen by the
   server at write time and frozen into a row, and a log rendered from the server's English
   would put the one screen somebody opens after an incident in the wrong language.

   A token with no sentence falls back to ITSELF rather than to a blank — `token()` below —
   and `app/contracts/activity-values.json` plus its test are what stop that ever being
   visible. But the fallback matters anyway: **on this screen a missing translation must
   never hide what happened.**
   ========================================================================= */

/** How far back, as the pack's own chip row. `undefined` means everything kept. */
type Range = 7 | 30 | 90 | undefined

const RANGES: readonly Range[] = [7, 30, 90, undefined]

/** The three tabs, plus "all activity" which is the ABSENCE of a filter rather than a
 *  fourth value — see `ActivityCategory.Administration` on the server, which has no tab
 *  and is what "all" contains that the others do not. */
const TABS: readonly (ActivityFilter | undefined)[] =
  [undefined, 'PERMISSION_CHANGE', 'OVERRIDE', 'SIGN_IN_REFUSED']

export function ActivityLog() {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [filter, setFilter] = useState<ActivityFilter | undefined>(undefined)
  const [days, setDays] = useState<Range>(7)
  /* PAGES ARE APPENDED, not replaced. An audit log read by scrolling is one a person can
     keep their place in; a page-number control on a table whose newest row is at the top
     means the rows move under them as it is written to. */
  const [cursors, setCursors] = useState<number[]>([])
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<unknown>(null)

  const mayRead = may('admin.activity')

  const from = useMemo(() => {
    if (days === undefined) return undefined
    const d = new Date()
    d.setUTCDate(d.getUTCDate() - days)
    // A DATE, not an instant: the server makes the range inclusive of whole days, in one
    // place, so that this screen and the CSV export cannot disagree about what "the 14th"
    // means.
    return d.toISOString().slice(0, 10)
  }, [days])

  /* ONE QUERY PER PAGE, and **`useQueries` RATHER THAN `useQuery` IN A LOOP.**

     The first version mapped `useQuery` over the cursor list, which reads perfectly well
     and breaks the rules of hooks the instant somebody presses "show older": the array gets
     longer, React sees more hooks than the previous render, and the screen throws. It was
     invisible in the source and invisible on the first render — the only thing that found it
     was a test that pressed the button. `useQueries` is the supported shape for a list whose
     length is decided at render time.

     Each page stays cached on its own cursor, so changing tab and coming back does not
     re-read rows somebody already has — which on a keyset cursor is also the only way "show
     older" can be idempotent. */
  const pages: (number | undefined)[] = [undefined, ...cursors]

  const queries = useQueries({
    queries: pages.map((cursor) => ({
      queryKey: ['activity', filter ?? 'ALL', from ?? 'ALL', cursor ?? 'FIRST'],
      queryFn: ({ signal }: { signal: AbortSignal }) => readActivity(
        {
          ...(filter ? { filter } : {}),
          ...(from ? { from } : {}),
          ...(cursor !== undefined ? { cursor } : {}),
        },
        signal,
      ),
      enabled: mayRead,
      retry: false,
    })),
  })

  const first = queries[0]!
  const rows: ActivityRow[] = queries.flatMap((q) => q.data?.rows ?? [])
  const last = queries[queries.length - 1]!
  const nextCursor = last.data?.nextCursor ?? null
  const months = first.data?.retentionMonths ?? 24

  const reset = (change: () => void) => { setCursors([]); change() }

  /* THE EXPORT GOES THROUGH THE AUTHENTICATED CLIENT AND BECOMES A BLOB.

     A plain link cannot carry the session token, and the download must not be a second,
     unauthenticated door into two years of who did what. So the bytes are fetched the way
     every other request is, and handed to the browser as an object URL. */
  const exportCsv = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const { csvBlobUrl, fileName } = await downloadCsv(filter, from)
      const a = document.createElement('a')
      a.href = csvBlobUrl
      a.download = fileName
      a.click()
      // REVOKED, or the blob is held for the lifetime of the tab. Two years of rows is
      // megabytes, and somebody exporting four filters leaves four of them in memory.
      URL.revokeObjectURL(csvBlobUrl)
    } catch (e) {
      setExportError(e)
    } finally {
      setExporting(false)
    }
  }

  if (!mayRead) {
    return (
      <Card>
        <CardHeader title={t.actTitle} icon={<ScrollText className="size-4" />} />
        {/* PACK 5 DRAWS THIS AS ITS OWN STATE, beside "empty", and the distinction is the
            point: somebody shown an empty table would conclude that nothing has ever
            happened in the hotel — a lie the screen told rather than a rule it enforced. */}
        <NoPermissionState body={t.actNoPermissionBody} />
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        {/* THE CARD SAYS WHAT IS IN FRONT OF YOU, not what the screen is called. The shell
            header above already says "Activity log"; repeating it here produced the same
            "Reports above Reports" duplication the sidebar records having made once, and it
            was invisible until the screen was rendered and looked at. */}
        <CardHeader
          title={tabLabel(filter, t)}
          subtitle={pluralise(t.actEntries, rows.length, locale, formatCount(rows.length, locale))}
          icon={<ScrollText className="size-4" />}
          actions={
            <button
              type="button"
              onClick={() => { void exportCsv() }}
              disabled={exporting || rows.length === 0}
              className="inline-flex items-center gap-1.5 rounded-hk border border-border
                         px-2.5 py-1.5 text-xs font-medium hover:bg-muted cursor-pointer
                         disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="size-3.5" />
              {exporting ? t.actExporting : t.actExport}
            </button>
          }
        />

        <div className="flex flex-col gap-2.5 p-3.5">
          <div className="flex flex-wrap items-center gap-2">
            {TABS.map((tab) => (
              <FilterChip
                key={tab ?? 'ALL'}
                label={tabLabel(tab, t)}
                active={filter === tab}
                onClick={() => reset(() => setFilter(tab))}
              />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {RANGES.map((r) => (
              <FilterChip
                key={r ?? 'ALL'}
                label={rangeLabel(r, t)}
                active={days === r}
                onClick={() => reset(() => setDays(r))}
              />
            ))}
          </div>

          {/* THE RETENTION SENTENCE, ON THE SCREEN. It is the answer to "why is there
              nothing from last March", and the person asking is entitled to be told here
              rather than by somebody who remembers the number. */}
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            {t.actRetentionNote.replace('{months}', formatCount(months, locale))}
          </p>

          {exportError !== null && (
            <p className="text-[12px] text-[#b91c1c] dark:text-[#fca5a5]">
              {translateError(exportError, locale).message}
            </p>
          )}
        </div>

        {first.isPending ? (
          <LoadingState rows={6} label={t.actLoading} />
        ) : first.isError ? (
          <ErrorState
            title={t.actErrorTitle}
            body={translateError(first.error, locale).message}
            {...(translateError(first.error, locale).correlationId
              ? { correlationId: translateError(first.error, locale).correlationId }
              : {})}
            onRetry={() => { void first.refetch() }}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={t.actEmptyTitle}
            body={filter !== undefined || days !== undefined
              ? t.actEmptyFilteredBody : t.actEmptyBody}
          />
        ) : (
          <>
            <TableWrap>
              <Table className="min-w-[1000px]">
                <THead>
                  <tr>
                    <TH>{t.actColWhen}</TH>
                    <TH>{t.actColWho}</TH>
                    <TH>{t.actColWhat}</TH>
                    <TH>{t.actColSubject}</TH>
                    <TH>{t.actColBefore}</TH>
                    <TH>{t.actColAfter}</TH>
                    <TH>{t.actColWhere}</TH>
                  </tr>
                </THead>
                <TBody>
                  {rows.map((row) => (
                    <Row key={row.entryId} row={row} locale={locale} t={t} />
                  ))}
                </TBody>
              </Table>
            </TableWrap>

            {nextCursor !== null && (
              <div className="border-t border-border p-3 text-center">
                <button
                  type="button"
                  onClick={() => setCursors([...cursors, nextCursor])}
                  disabled={last.isFetching}
                  className="rounded-hk border border-border px-3 py-1.5 text-xs font-medium
                             hover:bg-muted cursor-pointer disabled:opacity-50"
                >
                  {t.actOlder}
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}

type Dict = (typeof STRINGS)['en']

function Row(
  { row, locale, t }: { row: ActivityRow; locale: 'en' | 'ar'; t: Dict },
) {
  return (
    <tr>
      {/* A TIMESTAMP IS A QUANTITY AND IS LOCALISED; everything beside it is not. */}
      <TD className="text-[12px] text-muted-foreground">
        {formatDateTime(new Date(row.atUtc), locale)}
      </TD>

      <TD wrap>
        <bdi>{actorLabel(row, locale, t)}</bdi>
      </TD>

      <TD wrap className="font-medium">
        {token(t.actVerb, row.verb)}
      </TD>

      <TD wrap>
        <span className="block text-[11px] text-muted-foreground">
          {token(t.actSubject, row.subjectType)}
        </span>
        {/* A ROOM NUMBER, A REFERENCE OR A TEMPLATE NAME. `Identifier` is what stops "404"
            being rendered as ٤٠٤ beside a door that says 404 — and `bdi` is what stops an
            Arabic name reordering the Latin text around it. */}
        <bdi className="block">
          {row.subjectType === 'ROOM'
            ? <Identifier>{row.subject}</Identifier>
            /* A SIGN-IN'S SUBJECT IS WHAT SOMEBODY TYPED — an employee number, or the word
               EMPTY when they typed nothing. That word is a TOKEN the server chose, so it
               needs a sentence, and without one it sat in Latin capitals in the middle of
               an Arabic table: #82's shape, found the way every one of them has been, by
               looking at the rendered Arabic screen.

               Only this subject type is looked up. Running every subject through the
               dictionary would translate a checklist template somebody had named CLEAN. */
            : row.subjectType === 'SIGN_IN'
              ? token(t.actValue as Record<string, string>, row.subject)
            : activitySubject(row, locale)}
        </bdi>
        {row.reason !== null && (
          <span className="mt-0.5 block text-[11px] text-muted-foreground">
            {t.actReasonLabel}: <bdi>{row.reason}</bdi>
          </span>
        )}
      </TD>

      <TD wrap className="text-[12px]">{value(row.before, t, locale)}</TD>
      <TD wrap className="text-[12px]">{value(row.after, t, locale)}</TD>

      <TD className="text-[12px] text-muted-foreground">
        {token(t.actSource, row.source)}
        {row.sourceDetail !== null && (
          /* THE DETAIL IS USUALLY A DEVICE CODE — `ANDROID`, `HANDSET_APP` — and `deviceLabel`
             is W-121's, shared rather than copied. It falls back to the code it was given,
             so a rule's free-text detail ("HVAC blocking") passes through unchanged and a
             code from a newer server is visibly untranslated rather than blank. */
          <span className="block text-[11px] opacity-80">
            <bdi>{deviceLabel(row.sourceDetail, locale)}</bdi>
          </span>
        )}
      </TD>
    </tr>
  )
}

/* ── turning the server's tokens into sentences ─────────────────────────────── */

/**
 * A token's sentence, falling back to the TOKEN rather than to a blank.
 *
 * The fallback is load-bearing on this screen and nowhere else. Everywhere else in the
 * product a missing string is a cosmetic defect; here it would be a row that says
 * somebody changed something, with the something missing. `ALLOWED_WITH_REASON` in the
 * wrong script is ugly and true; an empty cell is neither.
 */
function token(dictionary: Record<string, string>, key: string): string {
  return dictionary[key] ?? key
}

/**
 * The before and after columns, which hold a token where the value is one and a plain
 * value otherwise — `404`, `v7`, `-4`, `E-20001`, `1 assignable`.
 *
 * Null is drawn as an em dash, as the pack does: the acts with no before (adding a person,
 * refusing a sign-in) are real acts and the absence is the information.
 */
function value(raw: string | null, t: Dict, locale: 'en' | 'ar') {
  if (raw === null) return <span className="text-muted-foreground">—</span>

  const sentence = (t.actValue as Record<string, string>)[raw]
  if (sentence !== undefined) return <bdi>{sentence}</bdi>

  /* A DEVICE CODE, which a session that was ended puts in this column — see
     `UserAdminService.RecordSessionEndAsync` for why it is here rather than in the subject.
     W-121's function, shared: the codes already have sentences in both languages and a
     second switch would be a second list to keep in step. */
  const device = deviceLabel(raw, locale)
  if (device !== raw) return <bdi>{device}</bdi>

  /* NOT A TOKEN, so it is an identifier or a number somebody will compare against a piece
     of paper — a version number, a room number, a signed quantity. `Identifier` keeps it in
     Western digits for the same reason a room number stays 404 in Arabic. */
  return <Identifier>{raw}</Identifier>
}

/** "System" and "Unknown" are the two actors that are not people, and the server sends them
 *  as tokens for exactly that reason — a log that said "SYSTEM" in an Arabic table would be
 *  the fifth occurrence of a defect this project has now written a guard for. */
function actorLabel(row: ActivityRow, locale: 'en' | 'ar', t: Dict): string {
  const known = (t.actActor as Record<string, string>)[row.actor]
  return known ?? activityActor(row, locale)
}

function tabLabel(tab: ActivityFilter | undefined, t: Dict): string {
  if (tab === undefined) return t.actTabAll
  if (tab === 'PERMISSION_CHANGE') return t.actTabPermissions
  if (tab === 'OVERRIDE') return t.actTabOverrides
  return t.actTabSignIns
}

function rangeLabel(r: Range, t: Dict): string {
  if (r === 7) return t.actRange7
  if (r === 30) return t.actRange30
  if (r === 90) return t.actRange90
  return t.actRangeAll
}

/* ── the export ─────────────────────────────────────────────────────────────── */

/**
 * The bytes come from `@hk/core` through the authenticated client — a plain link could not
 * carry the session token, and a downloadable URL that did not need one would be a second,
 * unauthenticated door into two years of who did what.
 *
 * What is left here is BROWSER work: `URL.createObjectURL` and a synthetic anchor click do
 * not exist on the handset, and `@hk/core` is shared with it.
 */
async function downloadCsv(filter: ActivityFilter | undefined, from: string | undefined) {
  const q = { ...(filter ? { filter } : {}), ...(from ? { from } : {}) }
  const blob = await exportActivityCsv(q)

  return {
    csvBlobUrl: URL.createObjectURL(blob),
    fileName: activityExportName(q),
  }
}
